import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { DepotSummary } from '../api/client'
import { useHasPermission } from '../api/session'
import { useItemQuery } from '../hooks/useItemQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { geldtext } from './priceFormat'
import { DepotAnlegenDialog } from './DepotDialogs'
import s from './DepotPage.module.scss'

/**
 * Die Depots — eines je Konto oder Strategie, jedes mit eigener Kasse.
 *
 * Die Übersicht zeigt je Depot die vier Zahlen der Kasse und zwei Zähler;
 * alles Weitere steht auf der Seite des Depots. Angelegt wird hier, gelöscht
 * dort — wer löscht, soll vorher gesehen haben, was drin ist.
 */
export function DepotsPage() {
  const darfLesen = useHasPermission('depot:read')
  const darfSchreiben = useHasPermission('depot:write')
  const [anlegen, setAnlegen] = useState(false)
  const [tick, setTick] = useState(0)
  // `tick` steht in den Abhängigkeiten, ohne im Rumpf vorzukommen — eine
  // NEUE Funktion, damit der Effekt nach dem Anlegen erneut lädt.
  const laden = useCallback((signal: AbortSignal) => api.depots(signal), [tick])
  const depots = useItemQuery<DepotSummary[]>(laden, darfLesen)

  if (!darfLesen || depots.denied) {
    return <PermissionNotice permission="depot:read" subject="die Depots" />
  }

  return (
    <>
      <Card span={12} title="Depots"
            meta={darfSchreiben && (
              <Button variant="primary" onClick={() => setAnlegen(true)}>
                Depot anlegen
              </Button>
            )}
            help="Je Depot eine Kasse: Einlage, Kontostand, reserviert durch offene Kauforders, frei. Jede Order und jede Transaktion gehört zu genau einem Depot.">
        {depots.error && <ErrorBox>{depots.error}</ErrorBox>}
        {depots.loading && !depots.item && <Loading>lädt die Depots …</Loading>}
        {depots.item && depots.item.length === 0 && (
          <EmptyState title="kein Depot">
            Noch kein Depot. Mit „Depot anlegen" entsteht das erste — Name,
            Währung, Einlage und Gebühr je Order.
          </EmptyState>
        )}
        {depots.item && depots.item.length > 0 && (
          <div className={s.rollen}>
            <div className={`${s.tabelle} ${s.depots}`}>
              <div className={s.kopf}>
                <span>Depot</span><span>Währung</span><span>Einlage</span>
                <span>Kontostand</span><span>Reserviert</span><span>Frei</span>
                <span>Offene Orders</span><span>Positionen</span>
                <span>Gebühr</span>
              </div>
              {depots.item.map((d) => (
                <div key={d.id} className={s.zeile}>
                  <span className={s.text}>
                    <Link to={`/depots/${d.id}`}>{d.name}</Link>
                  </span>
                  <span className={s.stumm}>{d.currency}</span>
                  <span className={s.num}>{geldtext(d.kasse.cash)}</span>
                  <span className={`${s.num} ${Number(d.kasse.balance.amount) < 0 ? s.minus : ''}`}>
                    {geldtext(d.kasse.balance)}
                  </span>
                  <span className={s.num}>{geldtext(d.kasse.reserved)}</span>
                  <span className={`${s.num} ${s.fett} ${Number(d.kasse.free.amount) < 0 ? s.minus : ''}`}>
                    {geldtext(d.kasse.free)}
                  </span>
                  <span className={s.num}>{d.open_orders || '—'}</span>
                  <span className={s.num}>{d.holdings || '—'}</span>
                  <span className={s.num}>{geldtext(d.fee_per_order)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
      {anlegen && (
        <DepotAnlegenDialog
          onFertig={() => { setAnlegen(false); setTick((t) => t + 1) }}
          onAbbrechen={() => setAnlegen(false)} />
      )}
    </>
  )
}
